import { motion } from "framer-motion";
import { Brain, Sparkles, Database, Clock, TrendingUp, FileText } from "lucide-react";
import { useApp } from "@/context/AppProviders";

const kpiIcons = [Brain, Sparkles, Database, Clock];

const ROICockpit = () => {
  const { t } = useApp();
  const r = t.roi;

  return (
    <section
      id="roi"
      data-testid="roi-section"
      className="py-24 sm:py-32 border-t border-border bg-card/30"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.5 }}
          className="max-w-2xl"
        >
          <p className="font-mono text-sm uppercase tracking-widest text-blue-500">
            {r.label}
          </p>
          <h2 className="font-heading mt-3 text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight">
            {r.title}
          </h2>
          <p className="mt-4 text-base leading-relaxed text-muted-foreground">
            {r.subtitle}
          </p>
        </motion.div>

        {/* KPI grid */}
        <div className="mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {r.kpis.map((kpi, i) => {
            const Icon = kpiIcons[i];
            return (
              <motion.div
                key={kpi.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.4, delay: i * 0.07 }}
                data-testid={`roi-kpi-${i}`}
                className="rounded-sm border border-border bg-card p-6"
              >
                <div className="flex h-10 w-10 items-center justify-center rounded-sm border border-blue-600/30 bg-blue-600/10 text-blue-500">
                  <Icon className="h-5 w-5" strokeWidth={2} />
                </div>
                <h3 className="font-heading mt-4 text-base font-medium tracking-tight">
                  {kpi.name}
                </h3>
                <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
                  {kpi.desc}
                </p>
              </motion.div>
            );
          })}
        </div>

        {/* Trend + report */}
        <div className="mt-6 grid gap-4 md:grid-cols-2">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-40px" }}
            transition={{ duration: 0.4 }}
            className="rounded-sm border border-border bg-card p-6"
          >
            <div className="flex items-center gap-3">
              <TrendingUp className="h-5 w-5 text-blue-500" />
              <h3 className="font-heading text-lg font-medium tracking-tight">
                {r.trendTitle}
              </h3>
            </div>
            <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
              {r.trendDesc}
            </p>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-40px" }}
            transition={{ duration: 0.4, delay: 0.08 }}
            className="rounded-sm border border-border bg-card p-6"
          >
            <div className="flex items-center gap-3">
              <FileText className="h-5 w-5 text-blue-500" />
              <h3 className="font-heading text-lg font-medium tracking-tight">
                {r.reportTitle}
              </h3>
            </div>
            <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
              {r.reportDesc}
            </p>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default ROICockpit;
